import irmHeader from 'assets/images/irm-header.png';
import { patientBarcodeStrip, barcodeScriptBlock, resolveCoupleSpouseCodes } from 'utils/printBarcodes';

// ── Shared helpers (duplicated per repo convention — each print file owns these) ──

function amountInWords(amount) {
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Math.round(amount * 100));
  const paise = n % 100; const rupees = Math.floor(n / 100);
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (num) => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+toW(num%100) : '');
  };
  const inIndian = (num) => {
    if (num === 0) return 'Zero'; let out = '';
    if (num >= 10000000) { out += toW(Math.floor(num/10000000))+' Crore '; num %= 10000000; }
    if (num >= 100000)   { out += toW(Math.floor(num/100000))  +' Lakh ';  num %= 100000; }
    if (num >= 1000)     { out += toW(Math.floor(num/1000))    +' Thousand '; num %= 1000; }
    if (num > 0)         { out += toW(num); } return out.trim();
  };
  let out = inIndian(rupees);
  if (paise > 0) out += ` and ${toW(paise)} Paise`;
  return 'INR ' + out.trim();
}

const STATUS_LABEL = { OPEN: 'Open', USED: 'Used', EXPIRED: 'Expired' };
const STATUS_STYLE = {
  OPEN: 'background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7',
  USED: 'background:#ede7f6;color:#5e35b1;border:1px solid #ce93d8',
  EXPIRED: 'background:#f5f5f5;color:#757575;border:1px solid #e0e0e0',
};

/**
 * Print a Credit Note — branded to match printGrn/printBill/printReturnNote/
 * printDebitNote/printPaymentReceipt (irmHeader logo, Montserrat title, #b2d8de
 * bordered letterhead, A5 portrait, PAN/bank/seal footer).
 * cn : credit note object carrying `.original_bill` (bill_no, patient_*, bill_date)
 * ph : selected pharmacy object (company/letterhead details)
 */
export default function printCreditNote(cn, ph) {
  if (!cn) return;
  const PRIMARY = '#00798E';
  const LIGHT_BG = '#e0f4f7';
  const BORDER = '#b2d8de';
  const bill = cn.original_bill || {};
  const fmtD = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;

  // Barcodes get their own full-height column (rowspan) beside the
  // pharmacy/reference info, instead of being crammed under the patient
  // address — keeps both left columns a natural, even height.
  const { coupleCode, spouseCode } = resolveCoupleSpouseCodes(bill.contactInfo);
  const { html: barcodeHtml, initScript: barcodeInit } = patientBarcodeStrip({
    idPrefix: 'bc-cn',
    patientCode: bill.contactInfo?.external_code,
    coupleCode,
    spouseCode,
    documentCode: cn.credit_note_no,
    documentLabel: 'CN No',
  });
  const barcodeCell = barcodeHtml
    ? `<td rowspan="2" style="width:22%;padding:5px 8px;vertical-align:top">${barcodeHtml}</td>`
    : '';
  const leftColWidth = barcodeHtml ? '39%' : '50%';

  const body = `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">CREDIT NOTE</div>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER}">
      <tr>
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:11pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
          ${ph?.sub_title ? `<div style="font-size:7.5pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
          <div style="font-size:7pt;color:#333;margin-top:2px;line-height:1.5">${ph?.address || ''}${ph?.city ? ', '+ph.city : ''}${ph?.pincode ? ' - '+ph.pincode : ''}</div>
          ${ph?.dl_no ? `<div style="font-size:6.5pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
          ${ph?.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
          ${ph?.gstin_uin && ph.gstin_uin !== ph.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN/UIN : ${ph.gstin_uin}</div>` : ''}
          ${ph?.state ? `<div style="font-size:6.5pt;color:#555">State Name : ${ph.state}${ph.state_code ? ', Code : '+ph.state_code : ''}</div>` : ''}
          ${ph?.email ? `<div style="font-size:6.5pt;color:#555">E-Mail : ${ph.email}</div>` : ''}
          ${ph?.mobile ? `<div style="font-size:6.5pt;color:#555">Mobile : ${ph.mobile}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;vertical-align:top;${barcodeHtml ? `border-right:1px solid ${BORDER}` : ''}">
          <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">No.</td><td style="font-weight:700;padding-bottom:2px">${cn.credit_note_no}</td></tr>
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Date</td><td style="font-weight:700;padding-bottom:2px">${fmtD(cn.credit_note_date)}</td></tr>
            <tr><td style="color:#777;padding-right:8px;white-space:nowrap">Status</td><td><span style="padding:2px 7px;border-radius:3px;font-size:7pt;font-weight:700;${STATUS_STYLE[cn.status] || 'background:#eee;color:#555'}">${STATUS_LABEL[cn.status] || cn.status}</span></td></tr>
          </table>
        </td>
        ${barcodeCell}
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td style="width:${leftColWidth};border-right:1px solid ${BORDER};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Patient</div>
          <div style="font-weight:700;font-size:9pt">${bill.patient_name || '—'}</div>
          ${bill.patient_mobile ? `<div style="font-size:7.5pt;color:#555">Mob: ${bill.patient_mobile}</div>` : ''}
          ${bill.patient_age ? `<div style="font-size:7.5pt;color:#555">Age: ${bill.patient_age}${bill.patient_gender ? ' | '+bill.patient_gender : ''}</div>` : ''}
          ${bill.patient_address ? `<div style="font-size:7pt;color:#777">${bill.patient_address}</div>` : ''}
        </td>
        <td style="width:${leftColWidth};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Reference Bill</div>
          <div style="font-weight:700">${bill.bill_no || '—'}</div>
          <div style="font-size:7.5pt;color:#777">Date: ${fmtD(bill.bill_date)}</div>
        </td>
      </tr>
    </table>
    ${barcodeScriptBlock(barcodeInit)}
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none">
      <tr><th style="${CH}text-align:left">Description</th><th style="${CH}width:30%;text-align:right">Amount (₹)</th></tr>
      <tr><td style="${C}">Credit Note Amount</td><td style="${C}text-align:right;font-family:monospace;font-weight:700">${Number(cn.amount).toFixed(2)}</td></tr>
      ${Number(cn.amount_used) > 0 ? `<tr><td style="${C}color:#888">Amount Used</td><td style="${C}text-align:right;font-family:monospace;color:#888">- ${Number(cn.amount_used).toFixed(2)}</td></tr>` : ''}
      <tr style="background:${LIGHT_BG}"><td style="${C}font-weight:700">Balance Amount</td><td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:10.5pt;color:${PRIMARY}">${Number(cn.balance_amount).toFixed(2)}</td></tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount Chargeable (in words)</td>
        <td style="padding:4px 10px;font-style:italic">${amountInWords(Number(cn.balance_amount))} Only</td>
        <td style="padding:4px 7px;border-left:1px solid ${BORDER};font-size:7pt;color:#888;text-align:right;white-space:nowrap">E. &amp; O.E.</td>
      </tr>
    </table>
    ${cn.reason ? `<div style="margin-top:4px;padding:4px 8px;font-size:7.5pt;color:#666;font-style:italic;border:1px solid ${BORDER};border-radius:2px"><b>Reason:</b> ${cn.reason}</div>` : ''}
    <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:45%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.pan_no ? `<div style="margin-bottom:3px"><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
          ${ph?.declaration ? `<div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-top:${ph.pan_no?'4px':'0'};margin-bottom:2px">Declaration</div><div style="font-size:6.5pt;color:#555;line-height:1.5">${ph.declaration}</div>` : ''}
        </td>
        <td style="width:30%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.bank_name ? `
            <div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-bottom:3px">Company&apos;s Bank Details</div>
            <table style="font-size:7pt;border-collapse:collapse;width:100%">
              <tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Bank Name</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_name}</td></tr>
              ${ph.account_no ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">A/c No.</td><td style="font-weight:600;padding-bottom:2px">: ${ph.account_no}</td></tr>` : ''}
              ${ph.bank_branch ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Branch</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_branch}</td></tr>` : ''}
              ${ph.ifsc_code ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">IFS Code</td><td style="font-weight:600;padding-bottom:2px">: ${ph.ifsc_code}</td></tr>` : ''}
            </table>
          ` : ''}
        </td>
        <td style="width:25%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:16px">
          <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:16px">for ${ph?.name || ''}</div>
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 7px;border-right:1px solid ${BORDER};vertical-align:middle">
          <div style="height:30px;border:1px dashed #ccc;border-radius:3px;display:flex;align-items:center;justify-content:center;color:#ccc;font-style:italic;font-size:7pt">Authorised Seal</div>
        </td>
        <td style="padding:4px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
      </tr>
    </table>
  `;

  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#00798E;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Credit Note &mdash; ${cn.credit_note_no}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#00798E;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Credit Note ${cn.credit_note_no}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:484px;margin:0 auto">${body}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}
